import { Contact } from "@sunrun/experience-ui-components/lib/components/ContactCard";
import type {
  ContractReviewMessage,
  SplatCheckoutDocument,
} from "../../../../../amplify/backend/function/OfferExpCheckout/ts/public/types";
import {
  getMessagesByCategory,
  type ContractReviewCategory,
} from "utils/contractReviewUtils";

const formatContactName = (contact?: Contact) => {
  if (!contact) return "";
  return `${contact.customerFirstName ?? ""} ${
    contact.customerLastName ?? ""
  }`.trim();
};

/** Split a name into comparable tokens (lowercase, periods stripped from initials). */
const nameTokens = (name: string) =>
  name
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((token) => token.replace(/\./g, ""));

/** Lowercases, trims, strips periods, and sorts name tokens so "Jane Doe" and "Doe Jane" match. */
const normalizeName = (name: string) => nameTokens(name).sort().join(" ");

/**
 * True when names are equal after normalize, or share at least two tokens
 * (so middle initials / middle names do not block a first+last match).
 */
const namesMatch = (left: string, right: string) => {
  const leftTokens = nameTokens(left);
  const rightTokens = nameTokens(right);
  if (!leftTokens.length || !rightTokens.length) return false;

  if (normalizeName(left) === normalizeName(right)) return true;

  const rightSet = new Set(rightTokens);
  const shared = new Set(leftTokens.filter((token) => rightSet.has(token)));
  return shared.size >= 2;
};

/**
 * True when at least one left name matches at least one right name
 */
const anyNamesMatch = (leftNames: string[], rightNames: string[]) => {
  if (!leftNames.length || !rightNames.length) return false;
  return leftNames.some((leftName) =>
    rightNames.some((rightName) => namesMatch(leftName, rightName))
  );
};

const buildTitleMismatchMessage = (
  signerName: string,
  namesOnTitle: string[]
) =>
  `The name on the agreement, ${
    signerName || "-"
  }, does not match the owner name on title: ${
    namesOnTitle.length ? namesOnTitle.join(", ") : "-"
  }.`;

const getMostRecentAttachmentUrl = (
  documents: SplatCheckoutDocument[] | undefined
): string | undefined => {
  if (!documents?.length) return undefined;
  return [...documents].sort(
    (a, b) =>
      new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime()
  )[0]?.url;
};

const hasContractReviewBanners = (
  messages: ContractReviewMessage[],
  category: ContractReviewCategory,
  filter?: (message: ContractReviewMessage) => boolean
): boolean => {
  const byCategory = getMessagesByCategory(messages, category);
  return (filter ? byCategory.filter(filter) : byCategory).length > 0;
};

export {
  formatContactName,
  normalizeName,
  anyNamesMatch,
  buildTitleMismatchMessage,
  getMostRecentAttachmentUrl,
  hasContractReviewBanners,
};
